import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OnOffSwitch } from "@/components/OnOffSwitch";
import { LoginCard, LOGIN_DEFAULTS, mergeLogin } from "@/components/LoginCard";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const STYLES = [["normal", "Normal"], ["bold", "Bold"], ["italic", "Italic"], ["bold italic", "Bold italic"]];
const TEXTS = [["title", "App name / title"], ["welcome", "Welcome message"], ["instruction", "Sign-in instruction text"]];
const COLORS = [["card_bg", "Login card background"], ["page_bg", "Login page background (behind the card)"], ["button_bg", "Sign in button color"], ["button_text", "Sign in button text color"], ["site_bg", "Whole site background (all pages)"]];

const PRESETS = [
  { key: "burgundy", name: "Burgundy", main: "#6b1e2e", page: "#4a1420", card: "#ffffff", text: "#ffffff", body: "#475569" },
  { key: "navy", name: "Navy", main: "#1e3a8a", page: "#0f1f4d", card: "#ffffff", text: "#ffffff", body: "#475569" },
  { key: "forest", name: "Forest", main: "#166534", page: "#0b3d20", card: "#ffffff", text: "#ffffff", body: "#475569" },
  { key: "gold", name: "Gold", main: "#b8860b", page: "#5c4306", card: "#fffbeb", text: "#1f1600", body: "#57534e" },
];

function applyPreset(s, p) {
  return {
    ...s, card_bg: p.card, page_bg: p.page, site_bg: p.page, button_bg: p.main, button_text: p.text,
    title: { ...s.title, color: p.main }, welcome: { ...s.welcome, color: p.main }, instruction: { ...s.instruction, color: p.body },
  };
}

function Presets({ onPick }) {
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="login-presets">
      <span className="block font-semibold text-slate-900">Quick color themes</span>
      <span className="block text-sm text-slate-500">One click sets the login page and whole-site colors. Press Save to publish.</span>
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button key={p.key} type="button" onClick={() => onPick(p)} className="flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-100" data-testid={`login-preset-${p.key}`}>
            <span className="h-4 w-4 rounded-full border border-slate-300" style={{ backgroundColor: p.main }} />
            <span className="h-4 w-4 rounded-full border border-slate-300" style={{ backgroundColor: p.page }} />
            {p.name}
          </button>
        ))}
      </div>
    </div>
  );
}

function Color({ value, onChange, testid, allowEmpty }) {
  return (
    <span className="flex items-center gap-2">
      <input type="color" value={value || "#6b1e2e"} onChange={(e) => onChange(e.target.value)} className={`h-9 w-12 cursor-pointer rounded border border-slate-300 bg-white ${allowEmpty && !value ? "opacity-40" : ""}`} data-testid={testid} />
      {allowEmpty && value && <button type="button" className="text-xs font-semibold text-slate-500 underline" onClick={() => onChange("")} data-testid={`${testid}-reset`}>Default</button>}
      {allowEmpty && !value && <span className="text-xs text-slate-400">Default</span>}
    </span>
  );
}

function SizeInput({ value, onChange, testid }) {
  const [v, setV] = useState(String(value));
  useEffect(() => { setV(String(value)); }, [value]);
  const commit = () => { const n = Math.max(10, Math.min(72, Number(v) || 10)); setV(String(n)); onChange(n); };
  return <Input type="number" min="10" max="72" value={v} onChange={(e) => { setV(e.target.value); const n = Number(e.target.value); if (n >= 10 && n <= 72) onChange(n); }} onBlur={commit} className="h-9 w-20 bg-white text-slate-900" data-testid={testid} />;
}

function TextRow({ k, label, t, set }) {
  const upd = (patch) => set({ [k]: { ...t, ...patch } });
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid={`login-text-${k}`}>
      <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">{label}</span><OnOffSwitch checked={t.show} onChange={(v) => upd({ show: v })} testid={`login-${k}-show`} /></div>
      <Input value={t.text} onChange={(e) => upd({ text: e.target.value })} maxLength={200} className="h-10 bg-white text-slate-900" data-testid={`login-${k}-text`} />
      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
        <label className="flex items-center gap-1">Size <SizeInput value={t.size} onChange={(n) => upd({ size: n })} testid={`login-${k}-size`} />px</label>
        <select value={t.style} onChange={(e) => upd({ style: e.target.value })} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-slate-900" data-testid={`login-${k}-style`}>
          {STYLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Color value={t.color} onChange={(c) => upd({ color: c })} testid={`login-${k}-color`} />
      </div>
    </div>
  );
}

export function AdminLoginPage() {
  const { loginStyle, refreshLoginStyle } = useApp();
  const [s, setS] = useState(mergeLogin(loginStyle));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setS(mergeLogin(loginStyle)); }, [loginStyle]);
  const set = (patch) => setS((x) => ({ ...x, ...patch }));
  const save = async (body) => {
    setBusy(true);
    try { await api.put("/settings", { login_page: body }); await refreshLoginStyle(); toast.success("Login page saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="login-page-card">
      <div>
        <h3 className="font-heading text-lg font-bold text-slate-900">Login page</h3>
        <p className="text-sm text-slate-500">Change what people see on the sign-in page. The preview updates as you type; press Save to publish. Tip: page titles are white, so a light whole-site background can make them hard to read. Master Admin only.</p>
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3"><span><span className="block font-semibold text-slate-900">Logo</span><span className="text-sm text-slate-500">Uses the logo uploaded in Branding</span></span><OnOffSwitch checked={s.show_logo} onChange={(v) => set({ show_logo: v })} testid="login-logo-show" /></div>
          <Presets onPick={(p) => { setS((x) => applyPreset(x, p)); toast.message(`${p.name} theme applied — press Save to publish`); }} />
          {TEXTS.map(([k, l]) => <TextRow key={k} k={k} label={l} t={s[k]} set={set} />)}
          <div className="space-y-2 rounded-lg border border-slate-200 p-3">
            {COLORS.map(([k, l]) => (
              <div key={k} className="flex items-center justify-between gap-3 text-sm"><span className="font-semibold text-slate-800">{l}</span><Color value={s[k]} onChange={(c) => set({ [k]: c })} testid={`login-${k}`} allowEmpty={k === "page_bg" || k === "site_bg"} /></div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => save(s)} disabled={busy} data-testid="login-page-save">Save login page</Button>
            <Button variant="outline" onClick={() => setS(LOGIN_DEFAULTS)} data-testid="login-page-defaults">Back to defaults</Button>
          </div>
        </div>
        <div className="flex items-center justify-center rounded-xl p-6" style={{ backgroundColor: s.page_bg || s.site_bg || "hsl(var(--background))" }} data-testid="login-preview">
          <LoginCard s={s} preview />
        </div>
      </div>
    </section>
  );
}
